import { useNavigate, useLocation, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Flame, Heart, MessageCircle, CalendarDays, User, LogOut, Crown, UserPlus, ShieldCheck, Gift, MapPin, Megaphone, MailWarning, Coins, Trophy, Rocket, MessageSquarePlus, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/discover", label: "Discover", icon: Flame },
  { to: "/matches", label: "Matches", icon: Heart },
  { to: "/creators", label: "Creators", icon: Crown },
  { to: "/map", label: "Map", icon: MapPin },
  { to: "/events", label: "Calendar", icon: CalendarDays },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { to: "/wallet", label: "Wallet", icon: Coins },
  { to: "/referrals", label: "Invite", icon: Gift },
  { to: "/feedback", label: "Feedback", icon: MessageSquarePlus },
  { to: "/help", label: "Help", icon: LifeBuoy },
  { to: "/requests", label: "Requests", icon: UserPlus },
  { to: "/profile", label: "Profile", icon: User },
];

const MOBILE_NAV = [
  { to: "/discover", label: "Discover", icon: Flame },
  { to: "/matches", label: "Matches", icon: Heart },
  { to: "/events", label: "Calendar", icon: CalendarDays },
  { to: "/wallet", label: "Wallet", icon: Coins },
  { to: "/profile", label: "Profile", icon: User },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (to) => location.pathname.startsWith(to);

  const [announcement, setAnnouncement] = useState("");
  const [emailBanner, setEmailBanner] = useState(user && user.email_verified === false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    api.get("/site-config").then((r) => setAnnouncement(r.data.announcement || "")).catch(() => {});
  }, []);
  useEffect(() => { setEmailBanner(!!user && user.email_verified === false); }, [user]);

  const resendVerify = async () => {
    setResending(true);
    try { await api.post("/auth/resend-verification", { origin: window.location.origin }); toast.success("Verification email sent — check your inbox"); }
    catch { toast.error("Couldn't send — try again shortly"); }
    finally { setResending(false); }
  };

  return (
    <div className="min-h-screen relative z-10 md:flex" style={{ background: "#0A0A0A" }}>
      {/* Desktop side nav */}
      <aside className="hidden md:flex flex-col w-64 border-r border-[#2A2A2A] p-6 sticky top-0 h-screen">
        <Link to="/discover" data-testid="brand-logo" className="mb-12">
          <div className="overline">Members Only</div>
          <div className="font-heading text-3xl gold-text leading-none mt-1">Pleasure Empire</div>
        </Link>
        <nav className="flex flex-col gap-2 flex-1">
          {NAV.map(({ to, label, icon: Icon }) => (
            <button key={to} data-testid={`nav-${label.toLowerCase()}`} onClick={() => navigate(to)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${isActive(to) ? "bg-[#1A1A1A] text-[#D4AF37]" : "text-[#A0A0A0] hover:text-white"}`}>
              <Icon size={20} /> <span className="font-medium">{label}</span>
            </button>
          ))}
          {user?.is_creator && (
            <button data-testid="nav-creator-onboarding" onClick={() => navigate("/creator-onboarding")}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${isActive("/creator-onboarding") ? "bg-[#1A1A1A] text-[#D4AF37]" : "text-[#A0A0A0] hover:text-white"}`}>
              <Rocket size={20} /> <span className="font-medium">Creator Setup</span>
            </button>
          )}
          {user?.role === "admin" && (
            <button data-testid="nav-verifications" onClick={() => navigate("/admin/verifications")}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${isActive("/admin/verifications") ? "bg-[#1A1A1A] text-[#D4AF37]" : "text-[#A0A0A0] hover:text-white"}`}>
              <ShieldCheck size={20} /> <span className="font-medium">Verify Users</span>
            </button>
          )}
          {user?.role === "admin" && (
            <button data-testid="nav-command" onClick={() => navigate("/admin/command")}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${isActive("/admin/command") ? "bg-[#1A1A1A] text-[#D4AF37]" : "text-[#A0A0A0] hover:text-white"}`}>
              <Crown size={20} /> <span className="font-medium">Command Center</span>
            </button>
          )}
        </nav>
        <div className="border-t border-[#2A2A2A] pt-4">
          <div className="text-sm text-white font-medium truncate">{user?.name}</div>
          <div className="text-xs text-[#A0A0A0] truncate mb-3">{user?.role === "admin" ? "👑 Empire Admin" : user?.email}</div>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}
            className="flex items-center gap-2 text-sm text-[#A0A0A0] hover:text-[#E63946] transition-colors">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 pb-24 md:pb-0 min-h-screen">
        {emailBanner && (
          <div data-testid="email-verify-banner" className="bg-[#D4AF37]/10 border-b border-[#D4AF37]/30 px-6 py-2.5 flex items-center gap-3 text-sm text-[#D4AF37]">
            <MailWarning size={16} className="flex-shrink-0" />
            <span className="flex-1">Verify your email to secure your account.</span>
            <button data-testid="resend-verify" onClick={resendVerify} disabled={resending} className="underline hover:no-underline disabled:opacity-60">{resending ? "Sending…" : "Resend email"}</button>
          </div>
        )}
        {announcement && (
          <div data-testid="announcement-banner" className="gold-gradient text-black px-6 py-2.5 flex items-center gap-2 text-sm font-medium">
            <Megaphone size={16} className="flex-shrink-0" /> <span>{announcement}</span>
          </div>
        )}
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 flex justify-around items-center py-3 backdrop-blur-xl bg-black/80 border-t border-white/10">
        {MOBILE_NAV.map(({ to, label, icon: Icon }) => (
          <button key={to} data-testid={`mnav-${label.toLowerCase()}`} onClick={() => navigate(to)}
            className={`flex flex-col items-center gap-1 ${isActive(to) ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`}>
            <Icon size={22} /> <span className="text-[10px]">{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
